import { useNavigate, useParams } from "react-router-dom";
import { useMe } from "../../hooks/useMe";
import { useMySteps, usePlaysAtRisk, type PlaySummary } from "../../hooks/personaQueries";
import { useWorkspaceEvents } from "../../hooks/useWorkspaceEvents";

const QUEUE_PREVIEW = 5;

export function OperatorHome() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const { data: steps, isLoading: stepsLoading } = useMySteps();
  const { data: plays, isLoading: playsLoading } = usePlaysAtRisk(workspaceId);

  // Subscribing keeps `me-steps` and `plays` queries fresh as the underlying
  // data changes — fan-out lives in useWorkspaceEvents itself.
  useWorkspaceEvents(workspaceId, () => {});

  const grouped = groupByHealth(plays ?? []);
  const queuePreview = (steps ?? []).slice(0, QUEUE_PREVIEW);

  return (
    <div className="main-inner" data-testid="operator-home">
      <div className="greet-row">
        <span className="greet">Good afternoon, {firstName(me?.user.displayName)}.</span>
        <span className="greet-date">YOUR FLEET TODAY</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">ASSIGNED TO YOU</div>
        <div className="lede">
          <b>{steps?.length ?? 0}</b> step{steps?.length === 1 ? "" : "s"} in your queue ·{" "}
          {plays?.length ?? 0} plays running.
        </div>
      </div>

      <div className="section-h">
        <span className="ttl">YOUR QUEUE</span>
        <button
          type="button"
          className="btn"
          data-testid="operator-home-open-queue"
          onClick={() => navigate(`/workspaces/${workspaceId}/operator`)}
        >
          Open queue →
        </button>
      </div>
      {stepsLoading ? (
        <Skeleton />
      ) : queuePreview.length === 0 ? (
        <EmptyState message="Nothing assigned to you." />
      ) : (
        <div className="st-list">
          {queuePreview.map((s) => (
            <div
              key={s.id}
              className="st-row"
              data-testid={`operator-home-step-${s.id}`}
              onClick={() =>
                navigate(`/workspaces/${s.workspaceId}/plays/${s.playId}#step-${s.id}`)
              }
            >
              <div className="st-l">
                <span className={`pd-step-type pd-type-${s.type}`}>{s.type}</span>
              </div>
              <div className="st-b">
                <div className="st-s">{s.title}</div>
                <div className="st-p">{s.playTitle}</div>
              </div>
              <div className="st-r">
                <button type="button" className="btn primary">
                  Open
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="section-h">
        <span className="ttl">PLAYS BY HEALTH</span>
        <span className="meta">{plays?.length ?? 0} in flight</span>
      </div>
      {playsLoading ? (
        <Skeleton />
      ) : (plays?.length ?? 0) === 0 ? (
        <EmptyState message="No plays in flight." />
      ) : (
        <div className="op-plays">
          {(["red", "amber", "green"] as const).map((sev) => {
            const list = grouped[sev];
            if (list.length === 0) return null;
            return (
              <div key={sev} className={`op-play-group op-group-${sev}`}>
                <div className="op-group-h">
                  <span className={`op-sev op-sev-${sev}`} />
                  <span>{labelFor(sev)}</span>
                  <span className="op-group-c">{list.length}</span>
                </div>
                {list.map((p) => (
                  <div
                    key={p.id}
                    className="op-play-row"
                    onClick={() => navigate(`/workspaces/${workspaceId}/plays/${p.id}`)}
                  >
                    <div className="op-play-t">{p.title}</div>
                    {p.issue ? <div className="op-play-i">{p.issue}</div> : null}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function firstName(displayName?: string): string {
  if (!displayName) return "there";
  return displayName.split(/\s+/)[0] ?? displayName;
}

function labelFor(sev: "red" | "amber" | "green") {
  if (sev === "red") return "Needs intervention";
  if (sev === "amber") return "Watch";
  return "On track";
}

function groupByHealth(
  plays: readonly PlaySummary[],
): Record<"red" | "amber" | "green", PlaySummary[]> {
  const out: Record<"red" | "amber" | "green", PlaySummary[]> = { red: [], amber: [], green: [] };
  for (const p of plays) {
    const h = p.health ?? "green";
    out[h].push(p);
  }
  return out;
}

function Skeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[0, 1].map((i) => (
        <div
          key={i}
          style={{ height: 56, borderRadius: 8, background: "var(--bg-soft)", opacity: 0.6 }}
        />
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: 24,
        textAlign: "center",
        color: "var(--text-3)",
        border: "1px dashed var(--border)",
        borderRadius: 8,
        fontSize: 13,
      }}
    >
      {message}
    </div>
  );
}
